import axios from "axios";
import React, { useState, useEffect } from "react";
import {
  FaBook,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaAngleDown,
  FaAngleRight,
  FaUserTie,
  FaHandsHelping,
  FaUsers,
  FaMoneyBillWave,
  FaSchool,
  FaNetworkWired,
  FaHome,
  FaFileAlt,
  FaSmile,
  FaVideo,
  FaGift,
  FaComments,
  FaServer,
  FaUserCircle,
  FaListAlt,
  FaSitemap,
  FaPenAlt,
  FaNewspaper,
  FaThLarge,
  FaTachometerAlt
} from "react-icons/fa";
import { config } from "../../config";

const apiUrl = config.apiUrl;

const SidebarItem = ({ 
  icon: Icon, 
  title, 
  isOpen, 
  hasChildren, 
  onClick, 
  isSelected, 
  isSidebarOpen,
  menu,
  setSelectedMenu
}) => {
  const handleClick = () => {
    if (menu) {
      setSelectedMenu(menu);
    }
    onClick();
  };

  return (
    <li
      className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-blue-50 text-gray-700'}`}
      onClick={handleClick}
    >
      {Icon && <Icon className={`text-lg ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />}
      {isSidebarOpen && (
        <>
          <span className="ml-3">{title}</span>
          {hasChildren && (
            <span className="ml-auto">
              {isOpen ? <FaAngleDown /> : <FaAngleRight />}
            </span>
          )}
        </>
      )}
    </li>
  );
};

const SubItem = ({ 
  title, 
  onClick, 
  isSelected, 
  isSidebarOpen,
  emoji 
}) => {
  return (
    <li
      onClick={onClick}
      className={`flex items-center p-2 pl-8 rounded-lg cursor-pointer text-sm transition-all duration-200
        ${isSelected ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-blue-50 text-gray-600'}`}
    >
      {isSidebarOpen && (
        <>
          {emoji && <span className="mr-2">{emoji}</span>}
          <span>{title}</span>
        </>
      )}
    </li>
  );
};

const Sidebar = ({
  isSidebarOpen,
  selectedMenu,
  setSelectedMenu,
  toggleSidebar,
  navigate,
  isMobile
}) => {
  // State for all collapsible menus
  const [openMenus, setOpenMenus] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('openMenus');
    return saved ? JSON.parse(saved) : {
      pages: true,
      webinar: true,
    };
  });

  // Save open menus to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('openMenus', JSON.stringify(openMenus));
  }, [openMenus]);

  const toggleMenu = (menu) => {
    console.log(`Toggling menu: ${menu}, current state: ${openMenus[menu]}`);
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleMenuClick = (menu) => {
    setSelectedMenu(menu);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      await axios.post(`${apiUrl}/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.removeItem("token");
      localStorage.removeItem("selectedMenu");
      localStorage.removeItem("openMenus");
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Menu structure data
  const menuSections = [
    {
      title: "Dashboard",
      icon: FaTachometerAlt,
      menu: "dashboard",
    },
    {
      title: "MCQ Editor",
      icon: FaPenAlt,
      key: "pages",
      items: [
         {
          title: "Subjects",
          icon: FaUserCircle,
          key: "Subjects",
          items: [
            { title: "Add & View Subject", menu: "addSubject", emoji: "📝" },
            // { title: "Subject Overview", menu: "subjectOverView", emoji: "🦸‍♂️" }
          ]
        },
        // {
        //   title: "Modules & Lessons",
        //   icon: FaUserCircle,
        //   key: "Modules",
        //   items: [
        //     { title: "Add Module", menu: "addModule", emoji: "📝" },
        //     { title: "Module Overview", menu: "moduleOverView", emoji: "🦸‍♂️" }
        //   ]
        // },
        {
          title: "Questions",
          icon: FaHandsHelping,
          key: "Questions",
          items: [
            { title: "Create Questions", menu: "addQuestion", emoji: "📝" },
            { title: "Questions Overview", menu: "questionOverview", emoji: "🛠️" }
          ]
        },
       
      ]
    },
     
    {
      title: "Notes",
      icon: FaServer,
      key: "notes",
      items: [
        { title: "All Notes", menu: "addNote", emoji: "📝" },
        // { title: "Note Overview", menu: "noteOverview", emoji: "🔌" }
      ]
    },
     {
      title: "Flash Card",
      icon: FaServer,
      key: "flash",
      items: [
        // { title: "Add Flash Card", menu: "addFlash", emoji: "📝" },
        { title: "Flash Card Overview", menu: "flashOverview", emoji: "🔌" }
      ]
    },
    {
      title: "Users",
      menu: "users",
      icon: FaNetworkWired
    },
  ];

  return (
    <aside
      className={`bg-white shadow-lg md:shadow-sm fixed md:static z-30 top-0 left-0 h-full w-96 transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      style={{ top: isMobile ? '60px' : '0' }}
    >
      <div className="h-full overflow-y-auto py-4 px-4">
        {isSidebarOpen && (
          <div className="px-3 mb-4">
            <h2 className="text-xl font-bold text-gray-800">Admin Panel</h2>
            <p className="text-xs text-gray-500">Manage your content</p>
          </div>
        )}
        <ul className="space-y-1">
          {menuSections.map((section) => {
            if (section.items) {
              return (
                <div key={section.key || section.menu}>
                  <SidebarItem
                    icon={section.icon}
                    title={section.title}
                    isOpen={openMenus[section.key]}
                    hasChildren={true}
                    onClick={() => toggleMenu(section.key)}
                    isSelected={false}
                    isSidebarOpen={isSidebarOpen}
                    setSelectedMenu={setSelectedMenu}
                  />
                  {openMenus[section.key] && isSidebarOpen && (
                    <ul className="space-y-1 mt-1">
                      {section.items.map((item) => {
                        if (item.items) {
                          return (
                            <div key={item.key || item.menu}>
                              <SidebarItem
                                icon={item.icon}
                                title={item.title}
                                isOpen={openMenus[item.key]}
                                hasChildren={true}
                                onClick={() => toggleMenu(item.key)}
                                isSelected={false}
                                isSidebarOpen={isSidebarOpen}
                                setSelectedMenu={setSelectedMenu}
                              />
                              {openMenus[item.key] && isSidebarOpen && (
                                <ul className="space-y-1 mt-1">
                                  {item.items.map((subItem) => {
                                    if (subItem.items) {
                                      return (
                                        <div key={subItem.key || subItem.menu}>
                                          <SidebarItem
                                            icon={subItem.icon}
                                            title={subItem.title}
                                            isOpen={openMenus[subItem.key]}
                                            hasChildren={true}
                                            onClick={() => toggleMenu(subItem.key)}
                                            isSelected={false}
                                            isSidebarOpen={isSidebarOpen}
                                            setSelectedMenu={setSelectedMenu}
                                          />
                                          {openMenus[subItem.key] && isSidebarOpen && (
                                            <ul className="space-y-1 mt-1">
                                              {subItem.items.map((subSubItem) => (
                                                <SubItem
                                                  key={subSubItem.menu}
                                                  title={subSubItem.title}
                                                  onClick={() => handleMenuClick(subSubItem.menu)}
                                                  isSelected={selectedMenu === subSubItem.menu}
                                                  isSidebarOpen={isSidebarOpen}
                                                  emoji={subSubItem.emoji}
                                                />
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <SubItem
                                          key={subItem.menu}
                                          title={subItem.title}
                                          onClick={() => handleMenuClick(subItem.menu)}
                                          isSelected={selectedMenu === subItem.menu}
                                          isSidebarOpen={isSidebarOpen}
                                          emoji={subItem.emoji}
                                        />
                                      );
                                    }
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        } else {
                          return (
                            <SidebarItem
                              key={item.menu}
                              icon={item.icon}
                              title={item.title}
                              isOpen={false}
                              hasChildren={false}
                              onClick={() => handleMenuClick(item.menu)}
                              isSelected={selectedMenu === item.menu}
                              isSidebarOpen={isSidebarOpen}
                              menu={item.menu}
                              setSelectedMenu={setSelectedMenu}
                            />
                          );
                        }
                      })}
                    </ul>
                  )}
                </div>
              );
            } else {
              return (
                <SidebarItem
                  key={section.menu}
                  icon={section.icon}
                  title={section.title}
                  isOpen={false}
                  hasChildren={false}
                  onClick={() => handleMenuClick(section.menu)}
                  isSelected={selectedMenu === section.menu}
                  isSidebarOpen={isSidebarOpen}
                  menu={section.menu}
                  setSelectedMenu={setSelectedMenu}
                />
              );
            }
          })}
          
          {/* Logout */}
          <SidebarItem
            icon={FaSignOutAlt}
            title="Logout"
            isOpen={false}
            hasChildren={false}
            onClick={handleLogout}
            isSelected={false}
            isSidebarOpen={isSidebarOpen}
          />
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;